import { useState } from 'react'
import type { ReactElement } from 'react'

import { Button, ConfirmDialog, EmptyState, ErrorState, Select, Spinner, Table, Tag } from '../../components'
import type { TableColumn } from '../../components'
import { getErrorCode } from '../../lib/errorCode'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import styles from './AssignmentsPage.module.scss'

/**
 * Who is in which station group, from the two angles a manager works in: BY
 * GROUP (who is on the marmitas right now, put somebody in, take somebody out)
 * and BY PERSON (where is Luis assigned today).
 *
 * The group is the axis: there is no assignment to a machine or to a work-center
 * type anywhere on this screen, because there is no such thing in the domain —
 * the machine is what the operator picks when they take a manufacturing order.
 * Shifts are out of scope too: an assignment opens now and stays open until
 * somebody closes it, which is why the only date shown is "desde".
 *
 * Nothing here reads the MRP mirror — `app.station_group_assignments`,
 * `app.users` and `app.station_groups` are all ours — so there is no freshness
 * badge to show. That absence is deliberate, not an omission.
 */

/** A person as `assignments.listUsers` answers them: who they are, never what they may do. */
type AssignableUser = RouterOutputs['assignments']['listUsers'][number]
type StationGroup = RouterOutputs['stations']['listGroups'][number]
type Assignment = RouterOutputs['assignments']['listActiveByGroup'][number]

/** `validFrom` arrives as a real `Date` (superjson), so it is formatted, not parsed. */
const DAY_MONTH_YEAR = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/**
 * `listActiveByGroup` and `listActiveByUser` hand back the assignment rows with
 * NO names joined in — the API says so, and says why: this screen already holds
 * `assignments.listUsers` and `stations.listGroups`, and joining there would ship a second,
 * divergent copy of the names. So the resolution happens here, and a row whose
 * counterpart is missing is LABELLED rather than dropped: a member the screen
 * cannot name is still a member.
 */
const UNKNOWN_USER = 'Persona desconocida'
const UNKNOWN_GROUP = 'Grupo desconocido'

/**
 * Inactive rows are listed, not hidden: they are exactly what a manager comes to
 * fix. "cuenta inactiva" and not "inactivo/inactiva": the state belongs to the
 * account, so the wording is the same for everybody and carries no gender.
 */
const optionLabel = (name: string, active: boolean): string =>
  (active ? name : `${name} (cuenta inactiva)`)

/**
 * What removing somebody from a group says.
 *
 * It promises no history: the closed row stays in `app.station_group_assignments`
 * but nothing in the SPA reads it, and a confirmation that claims a record the
 * screens give no way to open is a claim nobody can check. What the manager can
 * verify is the part that is said instead — the assignment can be made again.
 */
const removeMemberMessage = (name: string, groupName: string): string =>
  `¿Quitar a ${name} del grupo ${groupName}? Podrás volver a asignarle este grupo más tarde.`

/**
 * What a refused assignment says. The generic copy for these two codes is too
 * vague to act on, and both have a fix the manager can carry out:
 *
 *  - `PRECONDITION_FAILED` — the person or the group is deactivated. Reactivating
 *    either (`users.reactivate`, `stations.updateGroup`) makes the same call work.
 *  - `CONFLICT` — somebody else already assigned them while this screen was open.
 */
const assignErrorMessage = (error: unknown): string => {
  const code = getErrorCode(error)
  if (code === 'PRECONDITION_FAILED') {
    return 'No se puede asignar: la persona o el grupo están desactivados. Reactívalos y vuelve a intentarlo.'
  }
  if (code === 'CONFLICT') {
    return 'Esa persona ya está asignada a este grupo.'
  }
  return trpcMessage(error)
}

/**
 * `unassign` answers `NOT_FOUND` when there is nothing to close, and that is not
 * a missing group: it is this screen showing a membership that somebody else
 * already ended. Saying so is more useful than "esto ya no existe".
 */
const unassignErrorMessage = (error: unknown): string =>
  getErrorCode(error) === 'NOT_FOUND'
    ? 'Esa persona ya no estaba asignada a este grupo. Vuelve a cargar la pantalla.'
    : trpcMessage(error)

type GroupMembersProps = {
  group: StationGroup
  /** Every user, inactive included — a member deactivated yesterday still needs a name. */
  users: Array<AssignableUser>
}

/**
 * The members of ONE group, with the two writes of this screen. Mounted with the
 * group's id as its `key`, so switching group remounts it and the half-filled
 * picker and the last error do not travel from one group to the next.
 */
const GroupMembers = ({ group, users }: GroupMembersProps): ReactElement => {
  const utils = trpc.useUtils()
  const [userToAssign, setUserToAssign] = useState('')
  const [memberToRemove, setMemberToRemove] = useState<Assignment | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const membersQuery = trpc.assignments.listActiveByGroup.useQuery({ stationGroupId: group.id })

  /**
   * Both writes move the same fact seen from two sides, so both lists go: this
   * group's members, and the groups of every person. The second is invalidated
   * without an input on purpose — the by-person view is not mounted right now,
   * and with a 30 s staleTime it would otherwise open showing the state before
   * the change.
   */
  const refreshAssignments = (): void => {
    void utils.assignments.listActiveByGroup.invalidate({ stationGroupId: group.id })
    void utils.assignments.listActiveByUser.invalidate()
  }

  const assign = trpc.assignments.assign.useMutation({
    onSuccess: () => {
      setUserToAssign('')
      refreshAssignments()
    },
    onError: (error) => setErrorMessage(assignErrorMessage(error)),
  })

  const unassign = trpc.assignments.unassign.useMutation({
    onSuccess: () => {
      setMemberToRemove(null)
      refreshAssignments()
    },
    onError: (error) => {
      // Close the dialog and put the reason on the screen behind it: leaving the
      // dialog open over an alert it does not show reads as a hung button.
      setMemberToRemove(null)
      setErrorMessage(unassignErrorMessage(error))
    },
  })

  if (membersQuery.isPending) {
    return <Spinner label="Cargando personas del grupo…" />
  }

  if (membersQuery.isError) {
    return <ErrorState error={membersQuery.error} onRetry={() => void membersQuery.refetch()} />
  }

  const members = membersQuery.data
  const userById = new Map(users.map((user) => [user.id, user]))
  const assignedIds = new Set(members.map((member) => member.userId))
  // Only ACTIVE people, and only those not already in: a deactivated person is
  // refused by the API, and offering them is offering a dead end.
  const assignable = users.filter((user) => user.active && !assignedIds.has(user.id))

  const nameOf = (userId: string): string => userById.get(userId)?.name ?? UNKNOWN_USER

  const columns: Array<TableColumn<Assignment>> = [
    {
      key: 'person',
      header: 'Persona',
      render: (row) => {
        const user = userById.get(row.userId)
        return (
          <span className={styles['assignments__cell-stack']}>
            {user?.name ?? UNKNOWN_USER}
            {user !== undefined && !user.active && <Tag tone="warn" label="Cuenta inactiva" />}
          </span>
        )
      },
    },
    {
      key: 'email',
      header: 'Email',
      hideOnMobile: true,
      render: (row) => userById.get(row.userId)?.email ?? '—',
    },
    {
      key: 'since',
      header: 'Desde',
      render: (row) => DAY_MONTH_YEAR.format(row.validFrom),
    },
    {
      key: 'remove',
      header: 'Acciones',
      align: 'right',
      render: (row) => (
        <Button
          variant="danger"
          label="Quitar"
          onClick={() => {
            setErrorMessage(null)
            setMemberToRemove(row)
          }}
        />
      ),
    },
  ]

  return (
    <>
      {!group.active && (
        <p className={styles.assignments__notice}>
          Este grupo está desactivado: no acepta asignaciones nuevas, pero sí quitar a quien siga en él.
        </p>
      )}

      <div className={styles.assignments__assign}>
        <div className={styles['assignments__assign-field']}>
          <Select
            label="Añadir persona al grupo"
            placeholder={assignable.length === 0 ? 'No queda nadie por asignar' : 'Elige una persona…'}
            value={userToAssign}
            disabled={assignable.length === 0}
            onChange={(event) => {
              setErrorMessage(null)
              setUserToAssign(event.target.value)
            }}
            options={assignable.map((user) => ({ value: user.id, label: user.name }))}
          />
        </div>
        <Button
          label={assign.isPending ? 'Asignando…' : 'Asignar'}
          disabled={userToAssign === '' || assign.isPending}
          onClick={() => {
            setErrorMessage(null)
            assign.mutate({ stationGroupId: group.id, userId: userToAssign })
          }}
        />
      </div>

      {errorMessage !== null && (
        <p role="alert" className={styles.assignments__error}>{errorMessage}</p>
      )}

      {members.length === 0
        ? (
          <EmptyState
            title="Nadie asignado a este grupo"
            message="Elige una persona arriba para asignarla."
          />
        )
        : (
          <Table
            columns={columns}
            rows={members}
            rowKey={(row) => row.id}
            caption={`Personas asignadas al grupo ${group.name}`}
          />
        )}

      <ConfirmDialog
        open={memberToRemove !== null}
        title="Quitar del grupo"
        message={
          memberToRemove === null
            ? ''
            : removeMemberMessage(nameOf(memberToRemove.userId), group.name)
        }
        variant="danger"
        confirmLabel="Quitar"
        isLoading={unassign.isPending}
        onCancel={() => setMemberToRemove(null)}
        onConfirm={() => {
          if (memberToRemove !== null) {
            setErrorMessage(null)
            unassign.mutate({ stationGroupId: group.id, userId: memberToRemove.userId })
          }
        }}
      />
    </>
  )
}

/** The by-group view: pick a group, work on who is in it. */
const GroupPanel = (): ReactElement => {
  const [stationGroupId, setStationGroupId] = useState('')

  const groupsQuery = trpc.stations.listGroups.useQuery({ includeInactive: true })
  const usersQuery = trpc.assignments.listUsers.useQuery()

  if (groupsQuery.isPending || usersQuery.isPending) {
    return <Spinner label="Cargando grupos y personas…" />
  }

  if (groupsQuery.isError) {
    return <ErrorState error={groupsQuery.error} onRetry={() => void groupsQuery.refetch()} />
  }

  if (usersQuery.isError) {
    return <ErrorState error={usersQuery.error} onRetry={() => void usersQuery.refetch()} />
  }

  const groups = groupsQuery.data

  if (groups.length === 0) {
    return (
      <EmptyState
        title="Aún no hay grupos"
        message="Crea un grupo de estaciones antes de asignar personas."
      />
    )
  }

  const selectedGroup = groups.find((group) => group.id === stationGroupId)

  return (
    <div className={styles.assignments__panel}>
      <div className={styles.assignments__picker}>
        <Select
          label="Grupo"
          placeholder="Elige un grupo…"
          value={stationGroupId}
          onChange={(event) => setStationGroupId(event.target.value)}
          options={groups.map((group) => ({ value: group.id, label: optionLabel(group.name, group.active) }))}
        />
      </div>

      {selectedGroup === undefined
        ? (
          <EmptyState
            title="Elige un grupo"
            message="Verás quién está asignado ahora mismo y podrás añadir o quitar personas."
          />
        )
        : <GroupMembers key={selectedGroup.id} group={selectedGroup} users={usersQuery.data} />}
    </div>
  )
}

type UserGroupsProps = {
  user: AssignableUser
  /** Every group, inactive included, to name the rows this person is in. */
  groups: Array<StationGroup>
}

/** Where ONE person is assigned right now. Read-only: the writes live by group. */
const UserGroups = ({ user, groups }: UserGroupsProps): ReactElement => {
  const groupsOfUser = trpc.assignments.listActiveByUser.useQuery({ userId: user.id })

  if (groupsOfUser.isPending) {
    return <Spinner label="Cargando grupos de la persona…" />
  }

  if (groupsOfUser.isError) {
    return <ErrorState error={groupsOfUser.error} onRetry={() => void groupsOfUser.refetch()} />
  }

  const assignments = groupsOfUser.data

  if (assignments.length === 0) {
    return (
      <EmptyState
        title={`${user.name} no está en ningún grupo`}
        message="Puedes asignarle un grupo desde la vista por grupo."
      />
    )
  }

  const groupById = new Map(groups.map((group) => [group.id, group]))

  const columns: Array<TableColumn<Assignment>> = [
    {
      key: 'group',
      header: 'Grupo',
      render: (row) => {
        const group = groupById.get(row.stationGroupId)
        return (
          <span className={styles['assignments__cell-stack']}>
            {group?.name ?? UNKNOWN_GROUP}
            {group !== undefined && !group.active && <Tag tone="warn" label="Desactivado" />}
          </span>
        )
      },
    },
    {
      key: 'since',
      header: 'Desde',
      render: (row) => DAY_MONTH_YEAR.format(row.validFrom),
    },
  ]

  return (
    <Table
      columns={columns}
      rows={assignments}
      rowKey={(row) => row.id}
      caption={`Grupos de ${user.name}`}
    />
  )
}

/** The by-person view: pick somebody, see where they are assigned. */
const UserPanel = (): ReactElement => {
  const [userId, setUserId] = useState('')

  const usersQuery = trpc.assignments.listUsers.useQuery()
  const groupsQuery = trpc.stations.listGroups.useQuery({ includeInactive: true })

  if (usersQuery.isPending || groupsQuery.isPending) {
    return <Spinner label="Cargando grupos y personas…" />
  }

  if (usersQuery.isError) {
    return <ErrorState error={usersQuery.error} onRetry={() => void usersQuery.refetch()} />
  }

  if (groupsQuery.isError) {
    return <ErrorState error={groupsQuery.error} onRetry={() => void groupsQuery.refetch()} />
  }

  const users = usersQuery.data

  if (users.length === 0) {
    return <EmptyState title="Aún no hay personas" message="Da de alta a alguien en Usuarios." />
  }

  const selectedUser = users.find((user) => user.id === userId)

  return (
    <div className={styles.assignments__panel}>
      <div className={styles.assignments__picker}>
        <Select
          label="Persona"
          placeholder="Elige una persona…"
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
          options={users.map((user) => ({ value: user.id, label: optionLabel(user.name, user.active) }))}
        />
      </div>

      {selectedUser === undefined
        ? <EmptyState title="Elige una persona" message="Verás los grupos en los que está ahora mismo." />
        : <UserGroups key={selectedUser.id} user={selectedUser} groups={groupsQuery.data} />}
    </div>
  )
}

type PanelView = 'group' | 'user'

const VIEW_LABEL: Record<PanelView, string> = {
  group: 'Por grupo',
  user: 'Por persona',
}

/**
 * The assignments screen. One view at a time, chosen with two big buttons rather
 * than a tab widget: this is a wall-mounted tablet, and both angles ask a
 * different question — showing them stacked puts two group pickers on the same
 * screen, which is the one way to assign somebody to the wrong group.
 */
export const AssignmentsPage = (): ReactElement => {
  const [view, setView] = useState<PanelView>('group')

  return (
    <section className={styles.assignments}>
      <header className={styles.assignments__header}>
        <h1 className={styles.assignments__title}>Asignaciones</h1>
        <div className={styles.assignments__views} role="group" aria-label="Vista">
          {(['group', 'user'] as Array<PanelView>).map((candidate) => (
            <Button
              key={candidate}
              variant={view === candidate ? 'primary' : 'secondary'}
              aria-pressed={view === candidate}
              onClick={() => setView(candidate)}
              label={VIEW_LABEL[candidate]}
            />
          ))}
        </div>
      </header>

      {view === 'group' ? <GroupPanel /> : <UserPanel />}
    </section>
  )
}
